import { useIntl } from '@/intl'
import { Item } from '@/scopes/common/components/Drawer/styled-components/'
import { DepartureAirportFilterSummary } from '@/scopes/search/components/DepartureAirportFilterComponent'
import { DepartureDateFilterSummary } from '@/scopes/search/components/DepartureDateFilter/DepartureDateFilterSummary'
import { DurationFilterSummary } from '@/scopes/search/components/DurationFilterComponent'
import { ParticipantsFilterSummary } from '@/scopes/search/components/ParticipantsFilterComponent'
import { hasActiveItemSelector, useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { useResults } from '@/scopes/search/search/domain/hooks'
import { DepartureAirportQuickSearchItemFilter } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureAiportFilter'
import { DepartureDateQuickSearchItemFilter } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DepartureDateFilter'
import { DurationQuickSearchItemFilter } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/DurationFilter'
import { ParticipantsQuickSearchItemFilter } from '@/scopes/search/search/pages/SearchPage/components/QuickSearchFilters/ParticipantsFilter'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, focusOutline, Icon, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import { PropsWithChildren } from 'react'
import { css, styled } from 'styled-components'

export const QuickSearchBarHeight = '76px'

const InputItems = styled.div``

const QuickSearchDrawer = styled.div<{ $hasActiveItem?: boolean, $isDisabled?: boolean, $isSticky?: boolean }>`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    max-width: 960px;
    box-shadow: ${tok('primitives_box_shadows_level_2')};
    background-color: ${tok('semantics_views_background_color')};
    border-radius: ${tokPx('components_quick_search_horizontal_corner_radius')};
    padding: ${space('primitives_space_8')};
    margin-left: ${tokPx('primitives_space_32')};
    margin-right: ${tokPx('primitives_space_32')};

    height: ${QuickSearchBarHeight};
    z-index: 1;
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-right: auto;
    margin-left: auto;
  }

  ${({ $isDisabled }) =>
    $isDisabled
    && css`
      pointer-events: none;

      ${InputItems} {
        pointer-events: none;
        opacity: ${tok('primitives_opacity_disabled')}%;
      }
    `}

  ${InputItems} {
    display: flex;
    flex-direction: column;

    @media ${mqMin('primitives_breakpoint_m_min')} {
      flex-direction: row;
      height: 100%;

      ${Item} {
        &:first-of-type {
          //using space instead of full radius due to incompatibility with the designs and the implementation (rest of the borders get cut off)
          border-radius: ${tokPx('components_quick_search_horizontal_field_first_and_last_corner_radius')}
            ${tokPx('components_quick_search_horizontal_field_corner_radius')}
            ${tokPx('components_quick_search_horizontal_field_corner_radius')}
            ${tokPx('components_quick_search_horizontal_field_first_and_last_corner_radius')};
        }

        &:last-of-type {
          //using space instead of full radius due to incompatibility with the designs and the implementation (rest of the borders get cut off)
          border-radius: ${tokPx('components_quick_search_horizontal_field_corner_radius')}
            ${tokPx('components_quick_search_horizontal_field_first_and_last_corner_radius')}
            ${tokPx('components_quick_search_horizontal_field_first_and_last_corner_radius')}
            ${tokPx('components_quick_search_horizontal_field_corner_radius')};
        }
      }
      /* =========================================
     Hover effect: adjusting border-radius when neighboring popovers are open
     ========================================= */
      ${Item}:hover {
        /* CASE 1: If the next sibling (to the right) is an open popover,
           remove the top-right and bottom-right border-radius of the hovered item
           so it visually connects seamlessly with the open popover. */
        &:has(+ ${Item}[data-state='open']) {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        /* CASE 2: If the previous sibling (to the left) is an open popover,
           remove the top-left and bottom-left border-radius of the hovered item
           for a seamless visual connection on the left side. */
        ${Item}[data-state='open'] + & {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      }

      /* =========================================
         Open popover effect: adjusting border-radius of neighboring items
         ========================================= */

      /* CASE 1: If the hovered sibling is to the right of the currently open popover,
         remove the left-side border-radius of the hovered sibling
         so it visually merges with the open popover. */
      ${Item}[data-state='open'] + ${Item}:hover {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }

      /* CASE 2: If the hovered sibling is to the left of the currently open popover,
         remove the right-side border-radius of the open popover
         so the two items visually connect on the right side. */
      ${Item}[data-state='open']:has(+ ${Item}:hover) {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      /* CASE 3: If the open popover is to the right of the hovered item,
         remove the left-side border-radius of the open popover
         to ensure a seamless visual connection. */
      ${Item}:hover + ${Item}[data-state='open'] {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
      }
    }
  }
`

const DrawerDesktop = styled(QuickSearchDrawer)`
  display: none;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: inherit;

    ${({ $isSticky }) => $isSticky && isStickyMod()}
  }
`

const Wrapper = styled.div<{ $hasPadding: boolean }>`
  width: 100%;
  justify-content: center;
  align-items: center;
  ${({ $hasPadding }) => $hasPadding && css`
    padding-bottom: ${space('primitives_space_48')};
    height: ${QuickSearchBarHeight};
  `}
`

function isStickyMod() {
  return css`
    left: 0;
    position: fixed;
    top: 0;
    width: 100vw;
    max-width: inherit;
    border-radius: inherit;

    ${InputItems} {
      justify-content: center;

      ${Item},
      ${Item}:first-of-type,
      ${Item}:last-of-type {
        border-radius: ${tokPx('components_quick_search_horizontal_field_corner_radius')};
      }
    }
  `
}

const ActionWrapper = styled.div`
  position: relative;
`

const ActionFade = styled.div`
  content: '';
  position: absolute;
  left: -${space('primitives_space_24')};
  width: 100%;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 40%);
  height: 100%;
`

const Action = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${space('primitives_space_44')};
  min-width: ${space('primitives_space_44')};
  background-color: ${tok('semantics_buttons_secondary_background_color')};
  border-radius: ${tokPx('semantics_buttons_m_corner_radius')};
  z-index: 1;
  position: relative;
`

const BarTrigger = styled.button<{ $isSticky?: boolean }>`
  align-items: center;
  display: flex;
  justify-content: space-between;
  width: calc(100% - 2 * ${space('primitives_space_16')});
  box-shadow: ${tok('primitives_box_shadows_level_2')};
  background-color: ${tok('semantics_views_background_color')};
  border-radius: ${tokPx('components_quick_search_horizontal_corner_radius')};
  padding: ${space('primitives_space_12')};
  padding-left: ${space('primitives_space_24')};
  margin-right: auto;
  margin-left: auto;
  cursor: pointer;
  gap: ${space('primitives_space_8')};

  ${({ $isSticky }) => $isSticky && isStickyMod()}

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: calc(100% - 2 * ${space('primitives_space_48')});

    ${({ $isSticky }) => $isSticky && isStickyMod()}
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: none;
  }

  &:hover {
    ${Action} {
      background-color: ${tok('semantics_buttons_secondary_hover_background_color')};
    }
  }

  ${focusOutline()}
`

const Box = styled.div`
  text-align: left;
  overflow: hidden;
`

const IconItems = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  gap: ${space('primitives_space_6')};

  ${Item} {
    padding: inherit;
    min-width: inherit;

    & > ${Icon} {
      display: none;
    }

    &:before {
      display: none;
    }
  }
`

export function QuickSearchBar({ disabled, className, hasPadding = true, showSticky }: PropsWithChildren<{ disabled?: boolean, className?: string, hasPadding?: boolean, showSticky: boolean }>) {
  const intl = useIntl()
  const hasActiveItem = useQuickSearchStore(hasActiveItemSelector)
  const { totalResults } = useResults()

  return (
    <Wrapper className={className} $hasPadding={hasPadding}>
      <DrawerDesktop
        $hasActiveItem={hasActiveItem}
        $isDisabled={disabled}
        $isSticky={showSticky}
      >
        <InputItems>
          <DepartureDateQuickSearchItemFilter id="departure-date-filter" />
          <DurationQuickSearchItemFilter id="travel-duration-filter" />
          <ParticipantsQuickSearchItemFilter id="participants-filter" />
          <DepartureAirportQuickSearchItemFilter id="departure-airport-duration-filter" />
        </InputItems>
      </DrawerDesktop>
      <Sheet.Root>
        <Sheet.Trigger>
          <BarTrigger $isSticky={showSticky}>
            <Box>
              <Typography variant="body-m-bold-tight">{intl.formatMessage({ id: 'quicksearch_title' })}</Typography>
              <IconItems>
                <DepartureDateFilterSummary />
                <DurationFilterSummary />
                <ParticipantsFilterSummary />
                <DepartureAirportFilterSummary />
              </IconItems>
            </Box>
            <ActionWrapper>
              <ActionFade />
              <Action>
                <Icon glyph="Pencil" />
              </Action>
            </ActionWrapper>
          </BarTrigger>
        </Sheet.Trigger>
        <Sheet.Body side="right" hasPadding>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'quicksearch_sheet_header' })}
          />
          <Sheet.Content>
            <QuickSearchDrawer>
              <InputItems>
                <DepartureDateQuickSearchItemFilter id="mobile-departure-date-filter" asSheet />
                <DurationQuickSearchItemFilter id="mobile-travel-duration-filter" asSheet />
                <ParticipantsQuickSearchItemFilter id="mobile-participants-filter" asSheet />
                <DepartureAirportQuickSearchItemFilter id="mobile-departure-duration-filter" asSheet />
              </InputItems>
            </QuickSearchDrawer>
          </Sheet.Content>
          <Sheet.Footer>
            <Sheet.Close>
              <Button variant="primary" fullWidth>
                {intl.formatMessage({ id: 'quicksearch_results' }, { nrOfResults: totalResults })}
              </Button>
            </Sheet.Close>
          </Sheet.Footer>
        </Sheet.Body>
      </Sheet.Root>
    </Wrapper>
  )
}
